import React, { useState, useEffect } from 'react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';
import { CreditCard, DollarSign, Download, ArrowRight } from 'lucide-react';

export const MyPayments: React.FC = () => {
    const [payments, setPayments] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [payLoading, setPayLoading] = useState(false);
    const { success, error: toastError } = useToast();
    
    // Derived states
    const unpaidAmount = payments.filter(p => p.status !== 'Paid').reduce((sum, p) => sum + (p.balance || p.amount), 0);

    useEffect(() => {
        fetchPayments();
    }, []);

    const fetchPayments = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/tenants/my-transactions');
            setPayments(response.data || []);
        } catch (err: any) {
            console.error('Error fetching payments:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePayNow = async () => {
        setPayLoading(true);
        try {
            // Trigger STK push or mock payment logic
            await apiClient.post('/tenants/pay-rent', { amount: unpaidAmount });
            success('Payment initiated', 'M-Pesa prompt sent to your phone. Please complete the transaction.');
            fetchPayments();
        } catch (err: any) {
            toastError('Payment failed', err.response?.data?.error || 'Payment initiation failed.');
        } finally {
            setPayLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-light-text dark:text-dark-text mb-6">My Payments</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-primary rounded-xl p-8 text-white shadow-md relative overflow-hidden">
                    <div className="absolute top-0 right-0 -mr-8 -mt-8 bg-white/10 w-32 h-32 rounded-full blur-2xl flex items-center justify-center pointer-events-none">
                        <DollarSign size={64} className="text-white/20" />
                    </div>
                    
                    <p className="text-white/80 text-lg mb-2 relative z-10">Total Balance Due</p>
                    <h2 className="text-4xl font-bold mb-4 relative z-10">
                        KES {unpaidAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </h2>
                    
                    <button 
                        onClick={handlePayNow}
                        disabled={unpaidAmount <= 0 || payLoading}
                        className={`mt-4 px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all relative z-10
                            ${unpaidAmount > 0 ? 'bg-white text-primary hover:bg-gray-100' : 'bg-white/30 text-white cursor-not-allowed'}
                        `}
                    >
                        {payLoading ? 'Processing...' : <>Pay with M-Pesa <ArrowRight size={18} /></>}
                    </button>
                    {unpaidAmount <= 0 && <p className="text-sm text-white/80 mt-2">You are all caught up!</p>}
                </div>
            </div>

            <div className="bg-light-card dark:bg-dark-card rounded-xl shadow-sm border border-light-border dark:border-dark-border p-6 mt-8">
                <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-6">Transaction History</h3>
                
                {loading ? (
                    <p className="text-center text-light-subtle dark:text-dark-subtle py-8">Loading transactions...</p>
                ) : payments.length === 0 ? (
                     <div className="text-center py-12">
                        <CreditCard size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                        <h3 className="text-lg font-medium text-light-text dark:text-dark-text mb-2">No transactions yet</h3>
                        <p className="text-light-subtle dark:text-dark-subtle">Your payment history will appear here.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-sm text-light-subtle dark:text-dark-subtle border-b border-light-border dark:border-dark-border">
                                    <th className="pb-3 font-medium px-4">Description</th>
                                    <th className="pb-3 font-medium px-4">Date</th>
                                    <th className="pb-3 font-medium px-4">Amount</th>
                                    <th className="pb-3 font-medium px-4">Status</th>
                                    <th className="pb-3 font-medium px-4 text-right">Receipt</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
                                {payments.map((p, i) => (
                                    <tr key={i} className="group hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
                                        <td className="py-4 px-4">
                                            <p className="font-medium text-sm text-light-text dark:text-dark-text">{p.description || 'Rent Payment'}</p>
                                        </td>
                                        <td className="py-4 px-4 text-sm text-light-subtle dark:text-dark-subtle">
                                            {new Date(p.created_at || p.date || p.dueDate).toLocaleDateString()}
                                        </td>
                                        <td className="py-4 px-4 font-medium text-sm text-light-text dark:text-dark-text">
                                            KES {p.amount?.toLocaleString()}
                                        </td>
                                        <td className="py-4 px-4">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium 
                                                ${p.status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {p.status || 'Pending'}
                                            </span>
                                        </td>
                                        <td className="py-4 px-4 text-right">
                                            {p.status === 'Paid' && (
                                                <button className="text-light-subtle dark:text-dark-subtle hover:text-primary transition-colors p-2 rounded-lg hover:bg-light-card dark:hover:bg-dark-card inline-block">
                                                    <Download size={18} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
